import { useRef, useEffect, KeyboardEvent } from 'react'

interface ChatInputProps {
  value: string
  onChange: (v: string) => void
  onSend: () => void
  isLoading: boolean
  remainingTokens: number
  tokensRefreshTime: number | null
  onTokenLimitHit: () => void
  /** True when the per-conversation message hard limit has been reached. */
  conversationAtLimit?: boolean
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`
  return n.toString()
}

export default function ChatInput({
  value,
  onChange,
  onSend,
  isLoading,
  remainingTokens,
  tokensRefreshTime,
  onTokenLimitHit,
  conversationAtLimit = false,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const tokenLimitReached = remainingTokens <= 0
  const isDisabled = isLoading || tokenLimitReached || conversationAtLimit

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [value])

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!isDisabled && value.trim()) {
        onSend()
      }
    }
  }

  function handleSendClick() {
    if (tokenLimitReached) { onTokenLimitHit(); return }
    if (conversationAtLimit) return
    if (!isLoading && value.trim()) onSend()
  }

  function formatRefreshDate(ts: number | null) {
    if (!ts) return null
    return new Date(ts * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  function getPlaceholder() {
    if (conversationAtLimit) return 'Conversation limit reached — start a new chat to continue.'
    if (tokenLimitReached) return 'Token limit reached. Please upgrade your plan.'
    return 'Ask a question about your notes…'
  }

  const borderClass = conversationAtLimit
    ? 'border-gray-200 bg-gray-50'
    : tokenLimitReached
    ? 'border-red-200 bg-red-50'
    : 'border-gray-300 bg-white focus-within:border-primary'

  return (
    <div className="border-t border-gray-200 bg-white px-4 py-3 flex-shrink-0">
      {/* Token / limit status */}
      <div className="flex items-center justify-between mb-2 text-xs">
        {conversationAtLimit ? (
          <span className="text-gray-400 italic">Start a new conversation to keep chatting.</span>
        ) : tokenLimitReached ? (
          <button
            onClick={onTokenLimitHit}
            className="text-red-600 hover:text-red-700 font-medium"
          >
            Token limit reached
            {tokensRefreshTime && ` · Resets ${formatRefreshDate(tokensRefreshTime)}`}
            {' '}· Upgrade →
          </button>
        ) : (
          <span className="text-gray-400">
            {formatNumber(remainingTokens)} tokens remaining
          </span>
        )}
        <span className="text-gray-300 text-xs">Shift+Enter for newline</span>
      </div>

      {/* Input row */}
      <div className={`flex items-end gap-2 rounded-xl border transition-colors ${borderClass}`}>
        <textarea
          ref={textareaRef}
          data-testid="chat-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          placeholder={getPlaceholder()}
          rows={1}
          className="flex-1 resize-none bg-transparent px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 focus:ring-offset-0 disabled:opacity-60 disabled:cursor-not-allowed"
        />
        <button
          data-testid="chat-send-btn"
          onClick={handleSendClick}
          disabled={isDisabled || !value.trim()}
          className="m-2 p-2 bg-primary text-white rounded-lg hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          title="Send (Enter)"
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </div>
    </div>
  )
}

